import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addDays, subDays, addWeeks, subWeeks, addMonths, subMonths, format } from 'date-fns';
import ExpertLayout from '../../components/ExpertLayout';
import { DayView } from '../../components/expert/calendar/DayView';
import { WeekView } from '../../components/expert/calendar/WeekView';
import { MonthView } from '../../components/expert/calendar/MonthView';
import { BookingDetailModal, CreateBookingPanel } from '../../components/expert/booking';
import { useCalendar } from '../../hooks/useCalendar';
import { expertDashboardApi } from '../../lib/http';

type ViewMode = 'day' | 'week' | 'month';

const ExpertDashboard: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateBookingOpen, setIsCreateBookingOpen] = useState(false);
  const [prefilledDate, setPrefilledDate] = useState<string | undefined>();
  const [prefilledTime, setPrefilledTime] = useState<string | undefined>();
  const [workingHoursEnd, setWorkingHoursEnd] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useCalendar(viewMode, currentDate);

  const { data: profile } = useQuery({
    queryKey: ['expert-profile'],
    queryFn: async () => {
      const response = await expertDashboardApi.profile.get();
      return response.data;
    },
  });

  const navigatePrevious = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDate(subDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(subWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(subMonths(currentDate, 1));
        break;
    }
  };

  const navigateNext = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDate(addDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(addWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(addMonths(currentDate, 1));
        break;
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleSlotClick = (slot: any, date?: string) => {
    // Open CreateBookingPanel with prefilled date and time
    const slotDate = date || format(currentDate, 'yyyy-MM-dd');
    const slotTime = slot.time || '';
    setPrefilledDate(slotDate);
    setPrefilledTime(slotTime);

    // Extract working hours end for the selected date
    let whEnd: string | null = null;
    if (data) {
      if (data.view_mode === 'day') {
        whEnd = data.working_hours?.end || null;
      } else if (data.view_mode === 'week') {
        const matchingDay = data.days.find(d => d.date === slotDate);
        whEnd = matchingDay?.working_hours?.end || null;
      }
    }
    setWorkingHoursEnd(whEnd);

    setIsCreateBookingOpen(true);
  };

  const handleNewBookingClick = () => {
    // Open CreateBookingPanel with empty fields
    setPrefilledDate(undefined);
    setPrefilledTime(undefined);
    setWorkingHoursEnd(null);
    setIsCreateBookingOpen(true);
  };

  const handleBookingClick = (bookingId: string) => {
    setSelectedBookingId(bookingId);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedBookingId(null);
  };

  const handleCreateBookingClose = () => {
    setIsCreateBookingOpen(false);
    setPrefilledDate(undefined);
    setPrefilledTime(undefined);
    setWorkingHoursEnd(null);
  };

  const handleBookingUpdated = () => {
    // Refetch calendar data when booking is updated
    refetch();
  };

  const handleBookingCreated = () => {
    // Refetch calendar data when booking is created
    refetch();
  };

  const handleDayClick = (date: string) => {
    setCurrentDate(new Date(date));
    setViewMode('day');
  };

  const formatDateRange = () => {
    switch (viewMode) {
      case 'day':
        return format(currentDate, 'EEEE, MMMM d');
      case 'week':
        const weekStart = new Date(currentDate);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return `${format(weekStart, 'dd')}-${format(weekEnd, 'dd MMMM')}`;
      case 'month':
        return format(currentDate, 'MMMM yyyy');
    }
  };

  const formatDateRangeDesktop = () => {
    switch (viewMode) {
      case 'day':
        return format(currentDate, 'MMMM d, yyyy');
      case 'week':
        const weekStart = new Date(currentDate);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
      case 'month':
        return format(currentDate, 'MMMM yyyy');
    }
  };

  return (
    <ExpertLayout>
      <div className="h-full flex flex-col bg-gray-50">
        {/* Header - Mobile optimized */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 md:px-6 md:py-4">
          {/* Mobile Layout */}
          <div className="md:hidden flex flex-col gap-3">
            {/* Row 1: View mode toggle (centered) */}
            <div className="flex justify-center">
              <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => setViewMode('day')}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                    viewMode === 'day'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Day
                </button>
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                    viewMode === 'week'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                    viewMode === 'month'
                      ? 'bg-white text-purple-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Month
                </button>
              </div>
            </div>

            {/* Row 2: Navigation arrows + Date (centered) */}
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={navigatePrevious}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors active:scale-95"
                aria-label="Previous"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-base font-semibold text-gray-900 min-w-[160px] text-center">
                {formatDateRange()}
              </span>
              <button
                onClick={navigateNext}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors active:scale-95"
                aria-label="Next"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:grid md:grid-cols-3 md:items-center">
            <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>

            {/* View mode toggle - centered */}
            <div className="flex justify-center">
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('day')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'day'
                      ? 'bg-white text-pink-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Day
                </button>
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'week'
                      ? 'bg-white text-pink-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'month'
                      ? 'bg-white text-pink-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Month
                </button>
              </div>
            </div>

            {/* New Booking button - right */}
            <div className="flex justify-end">
              <button
                onClick={handleNewBookingClick}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors shadow-sm"
              >
                + New Booking
              </button>
            </div>
          </div>
        </div>

        {/* Calendar Content */}
        <div className="flex-1 overflow-auto p-3 md:p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-pink-600"></div>
                <p className="mt-3 text-sm md:text-base text-gray-600">Loading calendar...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full px-4">
              <div className="text-center">
                <svg
                  className="mx-auto h-10 w-10 md:h-12 md:w-12 text-red-400 mb-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p className="text-base md:text-lg font-medium text-gray-900 mb-2">Error loading calendar</p>
                <p className="text-xs md:text-sm text-gray-600 mb-4">
                  {error instanceof Error ? error.message : 'An unexpected error occurred'}
                </p>
                <button
                  onClick={() => refetch()}
                  className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors active:scale-95"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : data ? (
            <>
              {data.view_mode === 'day' && (
                <DayView
                  data={data}
                  onSlotClick={handleSlotClick}
                  onBookingClick={handleBookingClick}
                  onNavigatePrevious={navigatePrevious}
                  onNavigateNext={navigateNext}
                  onGoToToday={goToToday}
                />
              )}
              {data.view_mode === 'week' && (
                <WeekView
                  data={data}
                  onSlotClick={handleSlotClick}
                  onBookingClick={handleBookingClick}
                  onDayClick={handleDayClick}
                  currency={profile?.currency}
                  onNavigatePrevious={navigatePrevious}
                  onNavigateNext={navigateNext}
                  onGoToToday={goToToday}
                />
              )}
              {data.view_mode === 'month' && (
                <MonthView
                  data={data}
                  onDayClick={handleDayClick}
                  currency={profile?.currency}
                  onNavigatePrevious={navigatePrevious}
                  onNavigateNext={navigateNext}
                  onGoToToday={goToToday}
                />
              )}
            </>
          ) : null}
        </div>

        {/* Booking Detail Modal */}
        <BookingDetailModal
          bookingId={selectedBookingId}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onBookingUpdated={handleBookingUpdated}
        />

        {/* Create Booking Panel */}
        <CreateBookingPanel
          isOpen={isCreateBookingOpen}
          onClose={handleCreateBookingClose}
          onSuccess={handleBookingCreated}
          prefilledDate={prefilledDate}
          prefilledTime={prefilledTime}
          timezone={data?.timezone}
          workingHoursEnd={workingHoursEnd}
        />
      </div>
    </ExpertLayout>
  );
};

export default ExpertDashboard;
